import { reduxForm, change, formValueSelector, isDirty } from 'redux-form'
import {
  manipulateMonths,
  manipulateDays,
  getEndOfWeek,
  formatDate,
  parse,
  isValidDate,
  DATETIME_FORMATS,
} from '@myap/ui-library/esm/date'
import {
  changeStep,
  fetchStateFundingAndPricing,
  setDistrictFundingSubsidyData,
} from '../../actions/funding'
import { StepButtons, DistrictFundsControls, DistrictFundingOtherFees } from '.'
import { Text, Date as DateSelector, OpenNewWindow } from '../common'
import {
  TOTAL_FUNDING_STEPS,
  FUNDING_SUBSIDY_CONFIGURATION_FORM_NAME,
  FUNDING_SUBSIDY_CONFIGURATION_FORM_FIELDS as FIELDS,
} from '../../constants/FundingConstants'
import { isEmpty } from '../../utils/common'
import { safeNumber } from '../../utils/numbers'
import styles from '../../assets/style/scss/funding.scss'

const FEDERAL_ASSIST_URL = 'https://apcentral.collegeboard.org/exam-administration-ordering-scores/ordering-fees/exam-fees/federal-state-assistance'
const EXAM_FEES_URL = 'https://apcentral.collegeboard.org/exam-administration-ordering-scores/ordering-fees/exam-fees'

const mapStateToProps = state => {
  const {
    user: {
      data: { selectedOrgId: orgId },
    },
    funding: {
      data: { subsidies },
    },
    stateFundingAndPricing: { fetched: fetchedStateFunding, data: stateFundingAndPricingData = {} },
    settingsEducationPeriod: { selectedEducationPeriod, availableEducationPeriods },
    settingsDeadlines: { data: deadlinesData },
  } = state
  const districtFundingLockDate = deadlinesData[selectedEducationPeriod]?.districtFundingLockDate
  const invoiceAvailableDate = deadlinesData[selectedEducationPeriod]?.invoiceAvailableDate
  const { examPricing = {}, stateCode = '' } = stateFundingAndPricingData
  const selector = formValueSelector(FUNDING_SUBSIDY_CONFIGURATION_FORM_NAME)
  const feeReducedTestPrice = safeNumber(examPricing?.reducedFee?.testPrice)
  const feeReducedStateFunding = safeNumber(examPricing?.reducedFee?.stateFunding)
  const standardFeeTestPrice = safeNumber(examPricing?.standardFee?.testPrice)
  const standardFeeStateFunding = safeNumber(examPricing?.standardFee?.stateFunding)
  const hasStateFunding =
    !isEmpty(examPricing?.reducedFee?.stateFunding) ||
    !isEmpty(examPricing?.standardFee?.stateFunding)

  const apAdminYear = availableEducationPeriods[selectedEducationPeriod]?.apAdminYear

  return {
    initialValues: {
      [FIELDS.feeReducedAmount.name]: safeNumber(subsidies?.reducedFee?.amount),
      [FIELDS.standardFeeAmount.name]: safeNumber(subsidies?.standardFee?.amount),
      [FIELDS.feeReducedId.name]: subsidies?.reducedFee?.id,
      [FIELDS.standardFeeId.name]: subsidies?.standardFee?.id,
      [FIELDS.feeReducedName.name]: subsidies?.reducedFee?.name,
      [FIELDS.feeReducedMustTake.name]: subsidies?.reducedFee?.courseTaken ?? false,
      [FIELDS.feeReducedCancellationFeesActive.name]:
        subsidies?.reducedFee?.feeTypeSubsidies?.cancellation?.active ?? false,
      [FIELDS.feeReducedCancellationFeesId.name]:
        subsidies?.reducedFee?.feeTypeSubsidies?.cancellation?.id ?? null,
      [FIELDS.feeReducedLateOrderFeesActive.name]:
        subsidies?.reducedFee?.feeTypeSubsidies?.lateOrder?.active ?? false,
      [FIELDS.feeReducedLateOrderFeesId.name]:
        subsidies?.reducedFee?.feeTypeSubsidies?.lateOrder?.id ?? null,
      [FIELDS.feeReducedLateTestingFeesActive.name]:
        subsidies?.reducedFee?.feeTypeSubsidies?.lateTesting?.active ?? false,
      [FIELDS.feeReducedLateTestingFeesId.name]:
        subsidies?.reducedFee?.feeTypeSubsidies?.lateTesting?.id ?? null,

      [FIELDS.standardFeeName.name]: subsidies?.standardFee?.name,
      [FIELDS.standardFeeMustTake.name]: subsidies?.standardFee?.courseTaken ?? false,
      [FIELDS.standardFeeCancellationFeesActive.name]:
        subsidies?.standardFee?.feeTypeSubsidies?.cancellation?.active ?? false,
      [FIELDS.standardFeeCancellationFeesId.name]:
        subsidies?.standardFee?.feeTypeSubsidies?.cancellation?.id ?? null,
      [FIELDS.standardFeeLateOrderFeesActive.name]:
        subsidies?.standardFee?.feeTypeSubsidies?.lateOrder?.active ?? false,
      [FIELDS.standardFeeLateOrderFeesId.name]:
        subsidies?.standardFee?.feeTypeSubsidies?.lateOrder?.id ?? null,
      [FIELDS.standardFeeLateTestingFeesActive.name]:
        subsidies?.standardFee?.feeTypeSubsidies?.lateTesting?.active ?? false,
      [FIELDS.standardFeeLateTestingFeesId.name]:
        subsidies?.standardFee?.feeTypeSubsidies?.lateTesting?.id ?? null,

      [FIELDS.invoiceDate.name]:
        subsidies?.invoiceAvailableDate && isValidDate(parse(subsidies?.invoiceAvailableDate))
          ? subsidies?.invoiceAvailableDate
          : `${apAdminYear}-06-30`,
    },
    currentValues: {
      [FIELDS.feeReducedAmount.name]: selector(state, FIELDS.feeReducedAmount.name),
      [FIELDS.standardFeeAmount.name]: selector(state, FIELDS.standardFeeAmount.name),

      [FIELDS.feeReducedName.name]: selector(state, FIELDS.feeReducedName.name),
      [FIELDS.feeReducedMustTake.name]: selector(state, FIELDS.feeReducedMustTake.name),
      [FIELDS.feeReducedCancellationFeesActive.name]: selector(
        state,
        FIELDS.feeReducedCancellationFeesActive.name
      ),
      [FIELDS.feeReducedLateOrderFeesActive.name]: selector(
        state,
        FIELDS.feeReducedLateOrderFeesActive.name
      ),
      [FIELDS.feeReducedLateTestingFeesActive.name]: selector(
        state,
        FIELDS.feeReducedLateTestingFeesActive.name
      ),

      [FIELDS.standardFeeName.name]: selector(state, FIELDS.standardFeeName.name),
      [FIELDS.standardFeeMustTake.name]: selector(state, FIELDS.standardFeeMustTake.name),
      [FIELDS.standardFeeCancellationFeesActive.name]: selector(
        state,
        FIELDS.standardFeeCancellationFeesActive.name
      ),
      [FIELDS.standardFeeLateOrderFeesActive.name]: selector(
        state,
        FIELDS.standardFeeLateOrderFeesActive.name
      ),
      [FIELDS.standardFeeLateTestingFeesActive.name]: selector(
        state,
        FIELDS.standardFeeLateTestingFeesActive.name
      ),

      [FIELDS.invoiceDate.name]: selector(state, FIELDS.invoiceDate.name),
    },
    [`minimum${FIELDS.feeReducedAmount.name}`]: 0,
    [`minimum${FIELDS.standardFeeAmount.name}`]: 0,
    orgId,
    hasStateFunding, // based on whether stateFunding is populated in any examPricing subsidies
    feeReducedTestPrice,
    feeReducedStateFunding,
    standardFeeTestPrice,
    standardFeeStateFunding,
    fetchedStateFunding,
    stateCode,
    academicYear: availableEducationPeriods[selectedEducationPeriod]?.descr,
    finalInvoiceDate: formatDate(
      manipulateMonths(availableEducationPeriods[selectedEducationPeriod]?.academicYrEndDate, 3)
    ),
    educationPeriodCd: selectedEducationPeriod,
    districtFundingLockDate,
    invoiceAvailableDate,
    dirty: isDirty(FUNDING_SUBSIDY_CONFIGURATION_FORM_NAME)(state),
  }
}

const ConfigureDistrictSubsidyFunding = ({
  orgId,
  educationPeriodCd,
  fetchedStateFunding,
  feeReducedTestPrice,
  feeReducedStateFunding,
  standardFeeTestPrice,
  standardFeeStateFunding,
  finalInvoiceDate,
  invoiceAvailableDate,
  initialValues,
  currentValues,
  hasStateFunding,
  stateCode,
  academicYear,
  districtFundingLockDate,
  dirty,
  handleSubmit,
  fetchStateFundingAndPricing,
}) => {
  const [feeReducedCost, setFeeReducedCost] = useState(null)
  const [standardFeeCost, setStandardFeeCost] = useState(null)
  const [stateFullName, setStateFullName] = useState('')

  const subsidyNameStyles = {
    display: 'inline-block',
    marginLeft: '20px',
    width: '500px',
  }

  const calcSchoolCost = (examCost = 0, stateFunds = 0, districtFunds = 0) =>
    examCost - stateFunds - safeNumber(districtFunds)

  const alertBeforeLeaving = e => {
    e.preventDefault()
    e.returnValue = ''
  }

  useEffect(() => {
    if (dirty) {
      window.addEventListener('beforeunload', alertBeforeLeaving)
    }
    return () => {
      if (dirty) {
        window.removeEventListener('beforeunload', alertBeforeLeaving)
      }
    }
  }, [])

  useEffect(() => {
    if (orgId && educationPeriodCd && !fetchedStateFunding) {
      fetchStateFundingAndPricing(orgId, educationPeriodCd)
    }
  }, [orgId, educationPeriodCd, fetchedStateFunding])

  useEffect(() => {
    const { name = '' } = APRICOT.data.states.find(s => s.value === stateCode) ?? {}
    setStateFullName(name === 'Choose a State' ? '' : name)
  }, [stateCode])

  // Calculate initial costs
  useEffect(() => {
    if (fetchedStateFunding) {
      setFeeReducedCost(
        calcSchoolCost(
          feeReducedTestPrice,
          feeReducedStateFunding,
          safeNumber(initialValues[FIELDS.feeReducedAmount.name])
        )
      )

      setStandardFeeCost(
        calcSchoolCost(
          standardFeeTestPrice,
          standardFeeStateFunding,
          safeNumber(initialValues[FIELDS.standardFeeAmount.name])
        )
      )
    }
  }, [fetchedStateFunding])

  if (fetchedStateFunding) {
    return (
      <>
        {hasStateFunding ? (
          <div className={styles.intro}>
            <p>
              The state of {stateFullName} is providing funding for AP Exams. For full details on
              state funding, visit{' '}
              <OpenNewWindow url={FEDERAL_ASSIST_URL}>
                Federal and State AP Exam Fee Assistance
              </OpenNewWindow>{' '}
              on AP Central.
            </p>
            <p>
              Below is a summary of your state&#39;s funding. Please indicate the amount your
              district will supplement for the following test taker types.
            </p>
            <p>
              For details on <OpenNewWindow url={EXAM_FEES_URL}>exam costs and fees</OpenNewWindow>,
              visit AP Central.
            </p>
          </div>
        ) : (
          <div className={`${styles.intro} ${styles['no-state-funding']}`}>
            <p>
              The state of {stateFullName} has not indicated funding for the {academicYear} school
              year yet. Please note: If you add funding now and it conflicts with your state&#39;s
              funding selection, you&#39;ll be contacted and required to adjust prior to{' '}
              {formatDate(districtFundingLockDate, DATETIME_FORMATS.longMonthDayYear)}. For full
              details on state funding, visit{' '}
              <OpenNewWindow url={FEDERAL_ASSIST_URL}>
                Federal and State AP Exam Fee Assistance
              </OpenNewWindow>{' '}
              on AP Central.
            </p>
            <p>
              For details on <OpenNewWindow url={EXAM_FEES_URL}>exam costs and fees</OpenNewWindow>,
              visit AP Central.
            </p>
          </div>
        )}
        <form className={styles['subsidy-configuration']} onSubmit={handleSubmit}>
          <div className={styles['fee-type']}>
            <h2 className={styles.header}>Fee-Reduced Students</h2>
            <div className={styles.content}>
              <div className={styles['exam-type']}>
                <h3 className={styles.header}>AP Exams</h3>
                <div className={styles.content}>
                  {!isEmpty(initialValues[FIELDS.feeReducedName.name]) ? (
                    <Text
                      name={FIELDS.feeReducedName.name}
                      isRequired={FIELDS.feeReducedName.required}
                      label="Name"
                      placeholder="Enter subsidy name"
                      inputStyle={subsidyNameStyles}
                    />
                  ) : null}
                  <div className={styles.pricing}>Exam Cost: ${feeReducedTestPrice}</div>
                  <div className={styles.pricing}>State funds: ${feeReducedStateFunding}</div>
                  <div className={styles.pricing}>
                    <DistrictFundsControls
                      amountField={FIELDS.feeReducedAmount.name}
                      mustTakeField={FIELDS.feeReducedMustTake.name}
                      formName={FUNDING_SUBSIDY_CONFIGURATION_FORM_NAME}
                      testPrice={feeReducedTestPrice}
                      stateFunding={feeReducedStateFunding}
                      updateCost={setFeeReducedCost}
                      calcNewCost={calcSchoolCost}
                    />
                  </div>
                  <div className={styles['pricing-total']}>
                    School pays: ${feeReducedCost} per qualifying exam
                  </div>
                  <DistrictFundingOtherFees
                    mustTakeField={FIELDS.feeReducedMustTake.name}
                    cancellationFeesField={FIELDS.feeReducedCancellationFeesActive.name}
                    lateOrderFeesField={FIELDS.feeReducedLateOrderFeesActive.name}
                    lateTestingFeesField={FIELDS.feeReducedLateTestingFeesActive.name}
                    hasStateFunding={hasStateFunding}
                    disabled={safeNumber(currentValues[FIELDS.feeReducedAmount.name]) < 1}
                    openByDefault={true}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={styles['fee-type']}>
            <h2 className={styles.header}>Standard Fee Students</h2>
            <div className={styles.content}>
              <div className={styles['exam-type']}>
                <h3 className={styles.header}>AP Exams</h3>
                <div className={styles.content}>
                  {!isEmpty(initialValues[FIELDS.standardFeeName.name]) ? (
                    <Text
                      name={FIELDS.standardFeeName.name}
                      isRequired={FIELDS.standardFeeName.required}
                      label="Name"
                      placeholder="Enter subsidy name"
                      inputStyle={subsidyNameStyles}
                    />
                  ) : null}
                  <div className={styles.pricing}>Exam Cost: ${standardFeeTestPrice}</div>
                  <div className={styles.pricing}>State funds: ${standardFeeStateFunding}</div>
                  <div className={styles.pricing}>
                    <DistrictFundsControls
                      amountField={FIELDS.standardFeeAmount.name}
                      mustTakeField={FIELDS.standardFeeMustTake.name}
                      formName={FUNDING_SUBSIDY_CONFIGURATION_FORM_NAME}
                      testPrice={standardFeeTestPrice}
                      stateFunding={standardFeeStateFunding}
                      updateCost={setStandardFeeCost}
                      calcNewCost={calcSchoolCost}
                    />
                  </div>
                  <div className={styles['pricing-total']}>
                    School pays: ${standardFeeCost} per qualifying exam
                  </div>
                  <DistrictFundingOtherFees
                    mustTakeField={FIELDS.standardFeeMustTake.name}
                    cancellationFeesField={FIELDS.standardFeeCancellationFeesActive.name}
                    lateOrderFeesField={FIELDS.standardFeeLateOrderFeesActive.name}
                    lateTestingFeesField={FIELDS.standardFeeLateTestingFeesActive.name}
                    hasStateFunding={hasStateFunding}
                    disabled={safeNumber(currentValues[FIELDS.standardFeeAmount.name]) < 1}
                  />
                </div>
              </div>
            </div>
          </div>
          <hr />
          <div className="row">
            <div className="col-xs-6">
              <DateSelector
                name={FIELDS.invoiceDate.name}
                isRequired={FIELDS.invoiceDate.required}
                form={FUNDING_SUBSIDY_CONFIGURATION_FORM_NAME}
                showLabel={true}
                label="When would you like to receive your invoice?"
                wrapperStyle={{ width: '175px' }}
                minDate={manipulateDays(getEndOfWeek(invoiceAvailableDate), 1)}
                maxDate={finalInvoiceDate}
                openToDate={currentValues[FIELDS.invoiceDate.name]}
              />
            </div>
          </div>
        </form>
        <StepButtons
          form={FUNDING_SUBSIDY_CONFIGURATION_FORM_NAME}
          previousStep={3}
          totalSteps={TOTAL_FUNDING_STEPS}
          formComplete={
            currentValues[FIELDS.feeReducedAmount.name] > 0 ||
            currentValues[FIELDS.standardFeeAmount.name] > 0 ||
            (!isEmpty(currentValues[FIELDS.invoiceDate.name]) && fetchedStateFunding)
          }
        />
      </>
    )
  }
  return null
}

export default connect(mapStateToProps, {
  changeStep,
  change,
  fetchStateFundingAndPricing,
  setDistrictFundingSubsidyData,
})(
  reduxForm({
    form: FUNDING_SUBSIDY_CONFIGURATION_FORM_NAME,
    enableReinitialize: true,
    validate: (
      vals,
      { feeReducedTestPrice, feeReducedStateFunding, standardFeeTestPrice, standardFeeStateFunding }
    ) => {
      const belowZeroErrorMsg = 'Amount that school pays cannot be less than zero'
      const errors = {}
      // Validate no resulting school costs are less than 0
      if (
        feeReducedTestPrice -
          feeReducedStateFunding -
          safeNumber(vals[FIELDS.feeReducedAmount.name]) <
        0
      ) {
        errors[FIELDS.feeReducedAmount.name] = belowZeroErrorMsg
      }
      if (
        standardFeeTestPrice -
          standardFeeStateFunding -
          safeNumber(vals[FIELDS.standardFeeAmount.name]) <
        0
      ) {
        errors[FIELDS.standardFeeAmount.name] = belowZeroErrorMsg
      }
      if (
        safeNumber(vals[FIELDS.feeReducedAmount.name]) === 0 &&
        safeNumber(vals[FIELDS.standardFeeAmount.name]) === 0
      ) {
        errors._error = 'At least one subsidy must be funded.'
      }
      return errors
    },
    onSubmit: (vals, dispatch, { changeStep, setDistrictFundingSubsidyData }) => {
      setDistrictFundingSubsidyData({
        reducedFee: {
          ...(vals[FIELDS.feeReducedName.name] ? { name: vals[FIELDS.feeReducedName.name] } : {}),
          ...(vals[FIELDS.feeReducedId.name] ? { id: vals[FIELDS.feeReducedId.name] } : {}),
          ...(vals[FIELDS.feeReducedAmount.name]
            ? { amount: safeNumber(vals[FIELDS.feeReducedAmount.name]) }
            : {}),
          feeTypeSubsidies: {
            cancellation: {
              id: vals[FIELDS.feeReducedCancellationFeesId.name],
              active: vals[FIELDS.feeReducedCancellationFeesActive.name],
            },
            lateTesting: {
              id: vals[FIELDS.feeReducedLateTestingFeesId.name],
              active: vals[FIELDS.feeReducedLateTestingFeesActive.name],
            },
            lateOrder: {
              id: vals[FIELDS.feeReducedLateOrderFeesId.name],
              active: vals[FIELDS.feeReducedLateOrderFeesActive.name],
            },
          },
          ...(vals[FIELDS.feeReducedMustTake.name]
            ? { courseTaken: vals[FIELDS.feeReducedMustTake.name] }
            : {}),
        },
        standardFee: {
          ...(vals[FIELDS.standardFeeName.name] ? { name: vals[FIELDS.standardFeeName.name] } : {}),
          ...(vals[FIELDS.standardFeeId.name] ? { id: vals[FIELDS.standardFeeId.name] } : {}),
          ...(vals[FIELDS.standardFeeAmount.name]
            ? { amount: safeNumber(vals[FIELDS.standardFeeAmount.name]) }
            : {}),
          feeTypeSubsidies: {
            cancellation: {
              id: vals[FIELDS.standardFeeCancellationFeesId.name],
              active: vals[FIELDS.standardFeeCancellationFeesActive.name],
            },
            lateTesting: {
              id: vals[FIELDS.standardFeeLateTestingFeesId.name],
              active: vals[FIELDS.standardFeeLateTestingFeesActive.name],
            },
            lateOrder: {
              id: vals[FIELDS.standardFeeLateOrderFeesId.name],
              active: vals[FIELDS.standardFeeLateOrderFeesActive.name],
            },
          },
          ...(vals[FIELDS.standardFeeMustTake.name]
            ? { courseTaken: vals[FIELDS.standardFeeMustTake.name] }
            : {}),
        },
        invoiceAvailableDate: vals[FIELDS.invoiceDate.name],
      })
      changeStep(5)
    },
  })(ConfigureDistrictSubsidyFunding)
)
